import { FC } from "react";

import { Box, Collapse, Stack } from "@mui/material";
import { TicketingFormCategory, canFormCategoryGenerateReceipt } from "@simplyk/common";
import { useController, useForm, useWatch } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useCurrentUserContext } from "../../../../../common/contexts/CurrentUserContext";
import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "../FormStepContainer";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardSwitch } from "@/dashboard-design-system/Switch";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

export type StepAdditionalDonationFormData = {
  extraDonation: boolean;
  generateExtraDonationReceipt: boolean;
};

export const TicketingLiveEditorStepAdditionalDonation: FC = () => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const canFormGenerateReceipt = canFormCategoryGenerateReceipt(organization?.country, apiTicketing.formCategory);

  const defaultValues: StepAdditionalDonationFormData = {
    extraDonation: apiTicketing.extraDonation,
    generateExtraDonationReceipt:
      Boolean(organization?.canGenerateReceipt) && canFormGenerateReceipt && apiTicketing.generateExtraDonationReceipt,
  };
  const form = useForm<StepAdditionalDonationFormData>({ defaultValues });

  const { field: extraDonationReceiptSwitchField } = useController({
    control: form.control,
    name: "generateExtraDonationReceipt",
  });

  const updateTicketingDonation = trpc.updateTicketingDonation.useMutation();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepAdditionalDonationFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      return await updateTicketingDonation.mutateAsync({
        ticketingId: apiTicketing.id,
        extraDonation: formData.extraDonation,
        generateExtraDonationReceipt: formData.extraDonation && formData.generateExtraDonationReceipt,
      });
    },
  });

  const isExtraDonationActive = useWatch({ control: form.control, name: "extraDonation" });
  const isExtraDonationReceiptActive = extraDonationReceiptSwitchField.value;
  const displayGenerateExtraDonationReceipt =
    isExtraDonationActive && organization?.canGenerateReceipt && canFormGenerateReceipt;
  const displayGiftAidExplanation = organization?.hasConnectedSwiftaidAtUtc;

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.ticketing.extraDonation = true; // We always display the extra donation switch in the preview
      liveDataToMutate.ticketing.generateExtraDonationReceipt =
        formData.extraDonation && formData.generateExtraDonationReceipt;
      liveDataToMutate.placeholders.isExtraDonation = !formData.extraDonation;
    },
  });

  const getStepDescription = (category: TicketingFormCategory): string => {
    switch (category) {
      case TicketingFormCategory.Event:
        return t("dashboard", "ticketing.collectDonation.event.description");
      case TicketingFormCategory.Shop:
        return t("dashboard", "ticketing.collectDonation.shop.description");
      case TicketingFormCategory.Auction:
        return t("dashboard", "ticketing.collectDonation.auction.description");
      case TicketingFormCategory.Lottery:
        return t("dashboard", "ticketing.collectDonation.lottery.description");
      case TicketingFormCategory.Sweepstakes:
        return t("dashboard", "ticketing.collectDonation.sweepstakes.description");
      case TicketingFormCategory.MembershipV2:
        return t("dashboard", "ticketing.collectDonation.membership.description");
      case TicketingFormCategory.Custom:
      default:
        return t("dashboard", "ticketing.collectDonation.custom.description");
    }
  };

  return (
    <FormStepContainer
      title={t("dashboard", "ticketing.collectDonation.title")}
      description={getStepDescription(apiTicketing.formCategory)}
      form={form}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
      }}
    >
      <LiveEditorBlock>
        <Stack direction="column">
          <LiveEditorToggle
            label={t("dashboard", "ticketing.collectDonation.label")}
            name="extraDonation"
            control={form.control}
            onValueChange={(checked) =>
              logUserActionEvent(
                checked ? AmplitudeEvents.FormExtraDonationAdded : AmplitudeEvents.FormExtraDonationRemoved
              )
            }
            data-test="ticketing-rates-extra-donation-switch"
          />

          <Collapse in={displayGenerateExtraDonationReceipt} timeout={300}>
            <Box
              sx={(theme) => ({
                marginTop: 2,
                padding: 1.5,
                border: `1px solid ${theme.palette.border.neutral.quiet}`,
                borderRadius: theme.radius(1),
              })}
            >
              <DashboardSwitch
                name="generateExtraDonationReceipt"
                label={t("dashboard", "ticketing.collectDonation.taxReceipt")}
                checked={isExtraDonationReceiptActive}
                ref={extraDonationReceiptSwitchField.ref}
                onChange={(_, checked) => extraDonationReceiptSwitchField.onChange(checked)}
                data-test="ticketing-rates-extra-donation-receipt-switch"
              />
            </Box>
          </Collapse>

          <Collapse in={Boolean(displayGiftAidExplanation)} timeout={300}>
            <DashboardTypography vibe="text-neutral-moderate" variant="body2" sx={{ marginTop: 1 }}>
              {t("dashboard", "ticketing.collectDonation.taxReceipt.giftaid")}
            </DashboardTypography>
          </Collapse>
        </Stack>
      </LiveEditorBlock>
    </FormStepContainer>
  );
};
